import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Pin, PinOff } from 'lucide-react';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getDefaultSize = (side, defaultSize) => {
  if (defaultSize) return defaultSize;
  if (side === 'bottom') return 200;
  return 280;
};

export default function Dock({
  id,
  side = 'left',
  title,
  badge,
  children,
  defaultSize,
  minSize = 180,
  maxSize = 600,
  defaultPinned = true,
  defaultCollapsed = false,
  collapsedLabel,
  className = '',
  headerActions,
  dataTourId,
}) {
  const storageKey = `hubDock:${id}`;
  const isHorizontal = side === 'left' || side === 'right';

  const [state, setState] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) || '{}');
      return {
        size: stored.size ?? getDefaultSize(side, defaultSize),
        pinned: stored.pinned ?? defaultPinned,
        collapsed: stored.collapsed ?? defaultCollapsed,
      };
    } catch {
      return {
        size: getDefaultSize(side, defaultSize),
        pinned: defaultPinned,
        collapsed: defaultCollapsed,
      };
    }
  });

  const persist = useCallback((next) => {
    setState((prev) => {
      const merged = { ...prev, ...next };
      localStorage.setItem(storageKey, JSON.stringify(merged));
      return merged;
    });
  }, [storageKey]);

  const resizeRef = useRef(null);
  const startRef = useRef({ pos: 0, size: 0 });

  const onResizeStart = (e) => {
    e.preventDefault();
    startRef.current = {
      pos: isHorizontal ? e.clientX : e.clientY,
      size: state.size,
    };

    const onMove = (moveEvent) => {
      const current = isHorizontal ? moveEvent.clientX : moveEvent.clientY;
      const delta = current - startRef.current.pos;
      let nextSize = startRef.current.size;

      if (side === 'left') nextSize = startRef.current.size + delta;
      else if (side === 'right') nextSize = startRef.current.size - delta;
      else nextSize = startRef.current.size - delta;

      persist({ size: clamp(nextSize, minSize, maxSize) });
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  useEffect(() => {
    resizeRef.current = null;
  }, [side]);

  if (!state.pinned && state.collapsed) {
    const CollapseIcon = side === 'left' ? ChevronRight : side === 'right' ? ChevronLeft : ChevronUp;
    return (
      <button
        type="button"
        onClick={() => persist({ collapsed: false, pinned: true })}
        className={clsx(
          'flex-shrink-0 flex flex-col items-center justify-center bg-surface border-surface-border text-xs text-gray-600 hover:bg-surface-hover transition-colors',
          side === 'bottom' ? 'h-8 w-full border-t' : 'w-8 h-full border-r',
          className
        )}
        title={`Open ${title}`}
      >
        <CollapseIcon className="h-3.5 w-3.5" />
        {collapsedLabel && (
          <span className="text-2xs mt-1 tabular-nums">{collapsedLabel}</span>
        )}
      </button>
    );
  }

  const sizeStyle = state.collapsed
    ? (isHorizontal ? { width: 32 } : { height: 32 })
    : (isHorizontal ? { width: state.size } : { height: state.size });

  const CollapseToggle = side === 'left' ? ChevronLeft : side === 'right' ? ChevronRight : ChevronDown;

  return (
    <div
      className={clsx(
        'flex-shrink-0 flex bg-surface border-surface-border overflow-hidden',
        side === 'left' && 'border-r flex-row',
        side === 'right' && 'border-l flex-row-reverse',
        side === 'bottom' && 'border-t flex-col',
        className
      )}
      style={sizeStyle}
      {...(dataTourId ? { 'data-tour-id': dataTourId } : {})}
    >
      {!state.collapsed && (
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <div className="hub-dock-header">
            <div className="flex items-center gap-2 min-w-0">
              <span className="hub-dock-title truncate">{title}</span>
              {badge != null && badge > 0 && (
                <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded bg-gray-900 text-white text-2xs font-semibold tabular-nums">
                  {badge}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {headerActions}
              <button
                type="button"
                onClick={() => persist({ pinned: !state.pinned })}
                className="p-1 rounded hover:bg-surface-hover text-gray-500"
                title={state.pinned ? 'Unpin dock' : 'Pin dock'}
              >
                {state.pinned ? <Pin className="h-3.5 w-3.5" /> : <PinOff className="h-3.5 w-3.5" />}
              </button>
              <button
                type="button"
                onClick={() => persist({ collapsed: true, pinned: state.pinned })}
                className="p-1 rounded hover:bg-surface-hover text-gray-500"
                title="Collapse"
              >
                <CollapseToggle className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-auto">
            {children}
          </div>
        </div>
      )}

      {state.collapsed && (
        <button
          type="button"
          onClick={() => persist({ collapsed: false })}
          className="flex-1 flex items-center justify-center hover:bg-surface-hover"
          title={`Expand ${title}`}
        >
          {side === 'left' && <ChevronRight className="h-4 w-4 text-gray-500" />}
          {side === 'right' && <ChevronLeft className="h-4 w-4 text-gray-500" />}
          {side === 'bottom' && <ChevronUp className="h-4 w-4 text-gray-500" />}
        </button>
      )}

      {!state.collapsed && (
        <div
          ref={resizeRef}
          role="separator"
          aria-orientation={isHorizontal ? 'vertical' : 'horizontal'}
          onMouseDown={onResizeStart}
          className={clsx(
            'flex-shrink-0 hover:bg-primary-200 transition-colors',
            isHorizontal ? 'w-1 cursor-col-resize' : 'h-1 cursor-row-resize',
            side === 'left' && 'order-last',
            side === 'bottom' && 'order-first'
          )}
        />
      )}
    </div>
  );
}
